/**
 * BackToPortfolioButton — shared "Back to my properties" pill for any
 * page a Property Manager can land on outside their dashboard
 * (Passport detail · booking funnel · /track/<ref>).
 *
 * Behaviour:
 *   • Probes /api/property-manager/me ONCE on mount. If it returns a
 *     partner the viewer is an approved PM → render the pill button.
 *   • Anyone else (anonymous, customer, lab tech, admin) → renders
 *     NOTHING so the pill doesn't bleed into non-PM flows.
 *   • Pill style matches the cyan primary-action palette used across
 *     the PM portal so the affordance feels native, not bolted on.
 *
 * Usage:
 *   <BackToPortfolioButton className="mb-4" />
 *
 * The pill always navigates to `/property-manager`. There's no
 * configurable destination because every PM page in the product is
 * exactly one click away from the PM home, and forcing the same
 * destination keeps the UX predictable.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * usePmAccess — shared hook that returns `true` once the current user
 * is confirmed as an approved Property Manager. Probes
 * `/api/property-manager/me` exactly once per session.
 *
 * Used by both `BackToPortfolioButton` and any caller that needs to
 * conditionally render PM-only chrome (eg hiding the customer
 * "All properties" link on the Passport page so PMs don't see two
 * back buttons).
 */
export function usePmAccess() {
  const { user } = useAuth();
  const [isPm, setIsPm] = useState(false);
  useEffect(() => {
    if (!user) { setIsPm(false); return; }
    // Jun 2026 — the auth payload now carries is_property_manager, so
    // regular customers never fire the probe (was 403 console noise).
    if (!user.is_property_manager) { setIsPm(false); return; }
    let cancelled = false;
    api.get("/property-manager/me")
      .then(({ data }) => { if (!cancelled) setIsPm(!!data?.partner); })
      .catch(() => { if (!cancelled) setIsPm(false); });
    return () => { cancelled = true; };
  }, [user]);
  return isPm;
}

export function BackToPortfolioButton({
  className = "",
  label = "Back to Managed Properties",
}) {
  const isPm = usePmAccess();

  if (!isPm) return null;

  return (
    <Link
      to="/property-manager"
      data-testid="back-to-pm-portfolio"
      className={
        "inline-flex items-center gap-2 px-4 py-2 rounded-full " +
        "bg-cyan-50 border border-cyan-300 text-cyan-800 font-semibold " +
        "text-sm hover:bg-cyan-600 hover:text-white hover:border-cyan-700 " +
        "transition-colors shadow-sm " + className
      }
    >
      <ArrowLeft className="w-4 h-4" />
      {label}
    </Link>
  );
}

export default BackToPortfolioButton;
